"use client"

/**
 * ProfileSkeleton — full-page skeleton for the redesigned user profile page.
 * Mirrors the new layout: full-screen hero, big stats, bento posts, achievements,
 * horizontal favorites scroll, recent activity timeline.
 */

function Bone({ className }: { className?: string }) {
  return <div className={`animate-pulse rounded-lg bg-muted/50 ${className ?? ""}`} />
}

function HeroSkeleton() {
  return (
    <div className="relative min-h-[85vh] flex flex-col justify-end overflow-hidden bg-secondary/20">
      <div className="px-6 md:px-12 lg:px-20 pb-12 md:pb-20">
        <Bone className="w-[60%] h-24 md:h-36 mb-8 rounded-xl!" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-4 flex items-end gap-6">
            <Bone className="w-28 h-28 md:w-36 md:h-36 rounded-full! shrink-0" />
            <div className="space-y-2">
              <Bone className="w-32 h-8 rounded-full!" />
              <Bone className="w-20 h-4" />
            </div>
          </div>
          <div className="lg:col-span-5 space-y-3">
            <Bone className="w-64 h-8" />
            <Bone className="w-80 h-5" />
          </div>
          <div className="lg:col-span-3 flex justify-start lg:justify-end">
            <Bone className="w-32 h-14 rounded-full!" />
          </div>
        </div>
      </div>
    </div>
  )
}

function StatsSkeleton() {
  return (
    <div className="py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-secondary/10">
      <div className="mb-16">
        <Bone className="w-32 h-4 mb-4" />
        <Bone className="w-72 h-12" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i}>
            <Bone className="w-full h-20 mb-2" />
            <Bone className="w-16 h-4" />
          </div>
        ))}
      </div>
    </div>
  )
}

function PostsSkeleton() {
  return (
    <div className="py-20 md:py-28 px-6 md:px-12 lg:px-20">
      <div className="mb-12">
        <Bone className="w-24 h-4 mb-4" />
        <Bone className="w-40 h-12" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className={`rounded-xl overflow-hidden ${i === 0 ? "lg:col-span-2 lg:row-span-2" : ""}`}
          >
            <Bone className={`w-full ${i === 0 ? "aspect-[16/10]" : "aspect-video"}`} />
            <div className="p-5 space-y-2">
              <Bone className="w-3/4 h-5" />
              <Bone className="w-full h-4" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function AchievementsSkeleton() {
  return (
    <div className="py-20 md:py-28 px-6 md:px-12 lg:px-20 border-t border-border">
      <div className="mb-12">
        <Bone className="w-28 h-4 mb-4" />
        <Bone className="w-48 h-12" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Bone key={i} className="h-40 rounded-xl!" />
        ))}
      </div>
    </div>
  )
}

function FavoritesSkeleton() {
  return (
    <div className="py-20 md:py-28 px-6 md:px-12 lg:px-20 border-t border-border">
      <div className="mb-12">
        <Bone className="w-24 h-4 mb-4" />
        <Bone className="w-36 h-12" />
      </div>
      <div className="flex gap-4 overflow-hidden">
        {Array.from({ length: 4 }).map((_, i) => (
          <Bone key={i} className="shrink-0 w-[280px] md:w-[320px] rounded-xl! aspect-[4/3]" />
        ))}
      </div>
    </div>
  )
}

function ActivitySkeleton() {
  return (
    <div className="py-20 md:py-28 px-6 md:px-12 lg:px-20 border-t border-border">
      <div className="mb-12">
        <Bone className="w-20 h-4 mb-4" />
        <Bone className="w-40 h-12" />
      </div>
      <div className="max-w-3xl space-y-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Bone key={i} className="h-16 rounded-xl!" />
        ))}
      </div>
    </div>
  )
}

export function ProfilePageSkeleton() {
  return (
    <div className="h-full w-full overflow-y-auto">
      <HeroSkeleton />
      <StatsSkeleton />
      <PostsSkeleton />
      <AchievementsSkeleton />
      <FavoritesSkeleton />
      <ActivitySkeleton />
    </div>
  )
}

/** Standalone skeleton for the recommended creators card */
export function RecommendedCreatorsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-center gap-2.5">
          <Bone className="w-9 h-9 rounded-full!" />
          <div className="flex-1 space-y-1.5">
            <Bone className="w-20 h-3.5" />
            <Bone className="w-14 h-2.5" />
          </div>
          <Bone className="w-12 h-7 rounded-full!" />
        </div>
      ))}
    </div>
  )
}
